import React, { useEffect, useRef } from 'react';
import parse from 'html-react-parser';
import { VIEW_LANGUAGES_SUPPORTED, VIEW_SYSTEM_RQMNTS } from '../data/Localization';

/**
 * Renders the product description component.
 *
 * @param {Object} props - The component props.
 * @param {string} props.longDescription - The long description of the product.
 * @param {string} props.systemRequirementUrl - The URL for viewing the system requirements of the product.
 * @param {string} props.languageSupportedUrl - The URL for viewing the supported languages of the product.
 * @param {string} props.ariaText - The ARIA text for accessibility.
 * @param {string} props.whatsIncluded - The content for what's included in the product.
 * @param {boolean} props.hidden - Indicates whether the component is hidden or not.
 * @returns {JSX.Element} The rendered product description component.
 */
const ProductDescription = (props) => {
	const contentRef = useRef(null);

	useEffect(() => {
		// Fix anchor tags to be relative to the current page
		const anchors = Array.from(contentRef?.current.querySelectorAll('a'));
		anchors.forEach((anchor) => {
			let currPath = anchor.getAttribute('href');
			if (currPath && !currPath.includes('http')) {
				const localRegx = /\/\w{2}-\w{2}/gm;
				const currPathName = window.location.pathname;
				const local = currPathName.match(localRegx) || '';
				currPath = currPath.startsWith('/') ? currPath : `/${currPath}`;
				anchor.setAttribute('href', `${window.location.origin}${local}${currPath}`);
			}
		});
	}, []);

	return (
		<div ref={contentRef} className={`prc__desc--content richtext-container ${props.hidden ? 'collapse' : ''}`}>
			<div className="prc__desc-show-more-left">
				{/* content here will come from a wisiwig */}
				{props.longDescription && parse(props.longDescription)}
				{/* end of content here will come from a wisiwig */}
				{(props.systemRequirementUrl || props.languageSupportedUrl) && (
					<ul className="prc__desc--links">
						{props.systemRequirementUrl && (
							<li className="prc__syst-req-link">
								<a
									href={props.systemRequirementUrl}
									target="_blank"
									rel="noreferrer"
									// eslint-disable-next-line no-undef
									aria-label={
										props.systemRequirementLabel
											? props.systemRequirementLabel
											: Granite.I18n.get('View system requirements for product', [props.ariaText])
									}>
									{props.systemRequirementLabel || VIEW_SYSTEM_RQMNTS}
								</a>
							</li>
						)}
						{props.languageSupportedUrl && (
							<li className="prc__lang-supp-link">
								<a
									href={props.languageSupportedUrl}
									target="_blank"
									rel="noreferrer"
									// eslint-disable-next-line no-undef
									aria-label={
										props.languageSupportedLabel
											? props.languageSupportedLabel
											: Granite.I18n.get('View supported languages for product', [props.ariaText])
									}>
									{props.languageSupportedLabel || VIEW_LANGUAGES_SUPPORTED}
								</a>
							</li>
						)}
					</ul>
				)}
			</div>
			<div className="prc__desc-show-more-right">{props.whatsIncluded && parse(props.whatsIncluded)}</div>
		</div>
	);
};

export default ProductDescription;
